import type { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";

/**
 * Shared rule for every surface that lets a user re-point a filter/condition
 * row at a DIFFERENT field: may the row keep its current operand?
 *
 * Written once because it was written wrong in four places independently. The
 * failure looks harmless and is not: "E-Mail = info@veltric.app" re-pointed at
 * an option-typed field left the address sitting in the option dropdown as a
 * phantom selection, and every save path in the repo happily persisted it.
 * Where the row is a role's data ACCESS SCOPE (role-template row filters) or a
 * bulk action's target set (batch check-in), a nonsense operand decides who
 * sees — or gets written to — what.
 *
 * The counter-risk is worse than the bug, so this discards ONLY on proof and
 * every "can't tell" case keeps the operand:
 *
 *  - **different data type** — free text cannot be an option key.
 *  - **different {@link Field.optionSource}** — offerings / treatments / lanes /
 *    rooms / email templates / campaigns / imports all present as MULTI_SELECT
 *    carrying opaque ObjectIds, so the data type says nothing about
 *    interchangeability. Their `options` are also `[]` for as long as the source
 *    query is in flight (useActivityFilterOptions maps `data ?? []`), so
 *    membership can never decide here. The source can.
 *  - **enumerated types** — every part of the operand must still be a real
 *    option of the target field; two SINGLE_SELECTs rarely share an option set.
 *
 * An UNRESOLVED field on either side (a deleted additional field, a key hidden
 * by the acting admin's own allowlist, or the half-typed string a picker
 * commits mid-typeahead) proves nothing — keep the operand.
 */

/** Field types whose operand is a fixed, enumerated set rather than free text. */
export const OPTION_VALUE_TYPES: ReadonlySet<DataType> = new Set([
  DataType.GUEST_STATUS,
  DataType.SINGLE_SELECT,
  DataType.MULTI_SELECT,
  DataType.BOOLEAN,
  DataType.EMAIL_STATUS,
]);

/** Empty operand — no selection, nothing to preserve or discard. */
export const isEmptyOperand = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value.length === 0 : !value;

/**
 * Operators whose operand is a SUBSTRING, not a whole value. `~` is the
 * row-filter glyph, `contains` / `not_contains` the condition-builder tokens;
 * all three reach Mongo as a case-insensitive `$regex` (main-backend
 * `FilterParser.buildFilterClause` case `"~"`). A substring is by definition
 * never equal to an option, so option-membership must NOT decide for them —
 * checking it discarded every working `Diet ~ "veg"` on sight.
 */
const SUBSTRING_OPERATORS: ReadonlySet<string> = new Set([
  "~",
  "contains",
  "not_contains",
]);

export function valueSurvivesFieldChange(
  prev: Field | undefined,
  next: Field | undefined,
  value: string | string[] | undefined,
  /** The row's operator. Omit only where the operand is not a filter operand
   *  (e.g. an import default value), which keeps the strict membership check. */
  operator?: string,
): boolean {
  if (isEmptyOperand(value)) return true;
  if (!prev || !next) return true;
  if (prev.dataType !== next.dataType) return false;
  if (prev.optionSource !== next.optionSource) return false;
  if (!OPTION_VALUE_TYPES.has(next.dataType)) return true;
  // Substring operands can never be options — see SUBSTRING_OPERATORS. The type
  // and source checks above already ran, which is the part that still applies.
  if (operator && SUBSTRING_OPERATORS.has(operator)) return true;
  // BOOLEAN has no field-owned `options` at all (its vocabulary lives in
  // TCCBoolean), and a custom select can legitimately have an empty list —
  // without a list to check against, keep the value. NOTE: GUEST_STATUS and
  // EMAIL_STATUS DO carry options (see default-returner), so they are decided
  // by membership below, not by this escape.
  const options = next.options;
  if (!options?.length) return true;
  const parts: string[] = Array.isArray(value) ? value : [value ?? ""];
  // "" is the "—" (is-empty) pick every filter value cell offers. It is a valid
  // operand against ANY option-typed field, so it must not veto a multi-pick
  // like ["Viktor", ""] that is otherwise entirely intact.
  return parts.every((p) => p === "" || options.includes(p));
}
